/**
 * Installer reports: list, generate, get artifact (download).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';

const QUERY_KEY = 'reports';

export interface ReportRow {
  id: string;
  org_id: string;
  type: 'availability' | 'alerts_incidents';
  params: { date_from?: string; date_to?: string; scope?: string; property_ids?: string[] };
  generated_at: string;
  generated_by: string | null;
}

export interface ReportArtifactRow {
  id: string;
  report_id: string;
  format: string;
  content_text: string | null;
  content_json: unknown;
  created_at: string;
}

export interface GenerateReportParams {
  type: 'availability' | 'alerts_incidents';
  date_from: string;
  date_to: string;
  scope: 'all' | 'selected';
  property_ids?: string[];
}

async function fetchReports(orgId: string | null): Promise<ReportRow[]> {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from('reports')
    .select('id, org_id, type, params, generated_at, generated_by')
    .eq('org_id', orgId)
    .order('generated_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as ReportRow[];
}

export function useReports() {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId],
    queryFn: () => fetchReports(orgId),
    enabled: Boolean(orgId),
  });
  return {
    reports: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useGenerateReport() {
  const client = useQueryClient();
  const { organization } = useApp();
  const mutation = useMutation({
    mutationFn: async (params: GenerateReportParams) => {
      const data = await invokeAuthedFunction<{
        success: boolean;
        report_id: string;
        type: string;
        params: unknown;
        generated_at: string;
      }>(supabase, 'generate-report', params);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return mutation;
}

export async function fetchReportArtifacts(reportId: string): Promise<ReportArtifactRow[]> {
  const { data, error } = await supabase
    .from('report_artifacts')
    .select('id, report_id, format, content_text, content_json, created_at')
    .eq('report_id', reportId);
  if (error) throw error;
  return (data ?? []) as ReportArtifactRow[];
}
